// Keep older preview answers readable without conflating the reason and progress.
export function passportAvailabilityDraft<T extends { reason: string; applicationStatus?: string }>(value: T): T {
  if (value.reason === "first_pending") return { ...value, reason: "no_passport", applicationStatus: "yes" };
  if (value.reason === "replacement_pending") return { ...value, reason: "", applicationStatus: "yes" };
  if (value.reason === "not_applied") return { ...value, reason: "no_passport", applicationStatus: "no" };
  return { ...value, applicationStatus: value.applicationStatus ?? "" };
}

export function validPassportDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value + "T00:00:00Z");
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function passportAvailabilityReady(
  value: { reason: string; applicationStatus?: string; applicationDate?: string; expedited?: string; expectedDate?: string },
  today = new Date().toLocaleDateString("en-CA"),
): boolean {
  if (!["no_passport", "expired", "lost_stolen", "expiring_soon"].includes(value.reason)) return false;
  if (value.applicationStatus === "no") return true;
  return Boolean(
    value.applicationStatus === "yes"
      && validPassportDate(value.applicationDate)
      && value.applicationDate <= today
      && ["yes", "no", "unknown"].includes(value.expedited ?? "")
      && (!value.expectedDate || (validPassportDate(value.expectedDate) && value.expectedDate >= value.applicationDate)),
  );
}

// Retain the local timing draft when toggling Yes/Not yet. Save only applicable values.
export function passportStatusAnswer<T extends Record<string, unknown>>(draft: T, applicationStatus: string): T {
  return { ...draft, applicationStatus, dateUnknown: false };
}

export function passportAvailabilitySaved<T extends { applicationStatus?: string }>(draft: T): T {
  return draft.applicationStatus === "no"
    ? { ...draft, applicationDate: "", expedited: "", expectedDate: "", applicationDateIsApproximate: false, dateUnknown: false }
    : { ...draft };
}

export function passportContinueTarget(step: number, editing: boolean, groupCount: number, reviewed = false) {
  if (step === 0 && reviewed) return { step, review: true };
  if (step === 0) return { step: 1, review: false };
  return editing || step >= groupCount - 1 ? { step, review: true } : { step: step + 1, review: false };
}
